
// Enhanced cache control utilities for ensuring fresh content delivery
export class CacheManager {
  private static readonly CACHE_VERSION = 'v2';
  private static readonly CACHE_PREFIX = 'redflags-app';

  // Add cache-busting parameters to URLs with deployment ID
  static addCacheBuster(url: string): string {
    const timestamp = Date.now();
    const deploymentId = import.meta.env.VITE_DEPLOYMENT_ID || 'dev';
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}_cb=${timestamp}&_dep=${deploymentId}`;
  }

  // Clear specific cache with enhanced logging
  static async clearCache(cacheName?: string): Promise<void> {
    if ('caches' in window) {
      try {
        if (cacheName) {
          const deleted = await caches.delete(cacheName);
          console.log(`Cache ${deleted ? 'cleared' : 'not found'}: ${cacheName}`);
        } else {
          const cacheNames = await caches.keys();
          console.log('Found caches:', cacheNames);
          const results = await Promise.all(
            cacheNames.map(async name => {
              const deleted = await caches.delete(name);
              console.log(`Cache ${deleted ? 'cleared' : 'failed to clear'}: ${name}`);
              return deleted;
            })
          );
          console.log(`✅ Cleared ${results.filter(Boolean).length}/${cacheNames.length} caches`);
        }
      } catch (error) {
        console.warn('Failed to clear cache:', error);
      }
    }
  }

  // Enhanced cache control headers
  static getCacheControlHeaders(): HeadersInit {
    return {
      'Cache-Control': 'no-cache, no-store, must-revalidate, max-age=0',
      'Pragma': 'no-cache',
      'Expires': '0',
      'Last-Modified': new Date().toUTCString()
    };
  }

  // Create a cache-aware fetch function with deployment tracking
  static async fetchWithCacheControl(url: string, options: RequestInit = {}): Promise<Response> {
    const headers = {
      ...this.getCacheControlHeaders(),
      ...options.headers
    };

    const finalUrl = this.addCacheBuster(url);
    console.log(`🌐 Fetching with cache control: ${finalUrl}`);

    return fetch(finalUrl, {
      ...options,
      headers,
      cache: 'no-cache'
    });
  }

  // Force reload with comprehensive cache invalidation
  static async forceReload(): Promise<void> {
    try {
      console.log('🔄 Starting force reload process...');
      
      // Clear all caches
      await this.clearCache();
      
      // Clear storage
      const localKeys = Object.keys(localStorage);
      const sessionKeys = Object.keys(sessionStorage);
      console.log('Clearing storage:', { localKeys, sessionKeys });
      
      localStorage.clear();
      sessionStorage.clear();
      
      // Unregister service workers
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        console.log('Unregistering service workers:', registrations.length);
        for (let registration of registrations) {
          await registration.unregister();
          console.log('Unregistered SW:', registration.scope);
        }
      }
      
      // Add deployment-specific cache buster to URL
      const deploymentId = import.meta.env.VITE_DEPLOYMENT_ID || Date.now();
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.set('_refresh', deploymentId.toString());
      
      console.log('🚀 Reloading to:', currentUrl.href);
      
      // Force reload with new URL
      window.location.href = currentUrl.href;
    } catch (error) {
      console.error('Failed to force reload:', error);
      // Fallback to simple reload
      window.location.reload();
    }
  }

  // Check if content is fresh with deployment awareness
  static isContentFresh(timestamp: number, maxAge: number = 300000): boolean {
    const age = Date.now() - timestamp;
    const isFresh = age < maxAge;
    console.log(`Content freshness check: ${age}ms old, fresh: ${isFresh}`);
    return isFresh;
  }
}

// Enhanced cache manager initialization
export const initializeCacheManager = () => {
  console.log('🧹 Initializing enhanced cache manager...');
  
  // Log current environment
  const env = import.meta.env.MODE;
  const deploymentId = import.meta.env.VITE_DEPLOYMENT_ID;
  console.log('Environment:', env, 'Deployment:', deploymentId);
  
  // Clear old caches on app start with better detection
  if ('caches' in window) {
    caches.keys().then(cacheNames => {
      console.log('Found existing caches:', cacheNames);
      cacheNames.forEach(cacheName => {
        // Clear old workbox/precache and version-specific caches
        if (cacheName.includes('workbox') || 
            cacheName.includes('precache') || 
            (cacheName.includes('redflags') && !cacheName.includes(deploymentId))) {
          caches.delete(cacheName).then(deleted => {
            console.log(`${deleted ? 'Cleared' : 'Failed to clear'} old cache: ${cacheName}`);
          });
        }
      });
    });
  }
  
  // Log page load performance
  if ('performance' in window) {
    window.addEventListener('load', () => {
      const timing = performance.timing;
      const loadTime = timing.loadEventEnd - timing.navigationStart;
      console.log(`📊 Page load time: ${loadTime}ms`);
    });
  }
};
